<template>
  <div class="event-videos-container">
    <DialogLayout
      v-if="videoDialogOpen"
      v-model="videoDialogOpen"
      width="900px"
      title="Video Clips"
    >
      <div
        v-if="loadingVideo"
        class="d-flex justify-center align-center pa-8"
        style="min-height: 300px"
      >
        <v-progress-circular indeterminate></v-progress-circular>
      </div>

      <video
        v-else-if="videoData"
        :key="videoKey"
        controls
        autoplay
        class="video-player"
      >
        <source :src="`data:video/mp4;base64,${videoData}`" type="video/mp4" />
      </video>
    </DialogLayout>

    <CardLayout title="Incident/Task Video" class="mt-4">
      <v-radio-group
        v-if="hasBothEventTypes"
        v-model="selectedEventType"
        row
        hide-details
        class="mt-0 mb-3"
      >
        <v-radio label="Motion Detection" value="een.motionDetectionEvent.v1" />
        <v-radio label="Person Detection" value="een.personDetectionEvent.v1" />
      </v-radio-group>

      <v-row v-if="loading">
        <v-col
          v-for="n in 3"
          :key="`skeleton-${n}`"
          cols="12"
          sm="6"
          md="4"
          class="pb-0"
        >
          <v-skeleton-loader
            type="card, image"
            height="150"
            class="event-thumbnail-skeleton"
          ></v-skeleton-loader>
        </v-col>
      </v-row>
      <v-row v-else-if="thumbnailsList.length > 0">
        <v-col
          v-for="(thumb, index) in thumbnailsList"
          :key="index"
          cols="12"
          sm="6"
          md="4"
          class="pb-0"
        >
          <v-card outlined class="event-thumbnail-card">
            <v-img
              v-if="thumb.thumbnail"
              :src="'data:image/jpeg;base64,' + thumb.thumbnail"
              height="150"
              class="event-thumbnail"
              @click="openVideo(thumb)"
            >
              <div class="video-overlay d-flex justify-center align-center">
                <v-btn
                  icon
                  color="white"
                  class="play-button"
                  @click.stop="openVideo(thumb)"
                >
                  <v-icon large>fas fa-play-circle</v-icon>
                </v-btn>
              </div>
            </v-img>
            <v-card-text class="pa-2">
              <div class="caption text-truncate">
                {{ formatTimestamp(thumb.startTimestamp) }}
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <div
        class="text-center mt-2"
        v-if="thumbnailsList.length > 0 && nextPageToken"
      >
        <v-btn
          color="primary"
          text
          :loading="loadingMore"
          @click="loadMoreThumbnails"
        >
          Load More
        </v-btn>
      </div>

      <div
        v-else-if="
          !loading &&
          noEventsDetected &&
          isIncidentWithinRetentionPeriod &&
          directHistoryUrl
        "
        class="grey lighten-3 d-flex justify-center align-center"
        style="height: 400px"
      >
        <div class="pa-2 position-relative" style="width: 100%; height: 400px">
          <iframe
            id="sd_direct_history_iframe_1"
            :key="'history-iframe-' + historyIframeKey"
            width="100%"
            height="400"
            :src="directHistoryUrl"
            allowfullscreen
            allow="autoplay"
            style="background-color: #000; width: 100%"
            @load="setupHistoryIframeListener"
          ></iframe>
        </div>
      </div>

      <div v-else-if="!loading && noEventsDetected && !isVideoCameraEnabled">
        <v-alert type="info" outlined>
          Video is not available. Videos are only retained for
          {{ eenVideoRetentionDays }} days.
        </v-alert>
      </div>
      <div
        v-else-if="
          !loading &&
          thumbnailsList.length === 0 &&
          !noEventsDetected &&
          !isVideoCameraEnabled
        "
      >
        <v-alert type="info" outlined> No video incident/task video </v-alert>
      </div>
    </CardLayout>
  </div>
</template>

<script>
import { EEN_VIDEO_FEED, GET_ALL_EEN_EVENTS } from "../smart_zones/graphql";
import { EEN_AUTH_STATUS } from "../fixtures/graphql";
import useFacility from "../authentication/useFacility";
import DialogLayout from "@layout/DialogLayout.vue";
import { GET_TASK_INCIDENTS } from "./graphql";
import CardLayout from "@layout/CardLayout.vue";
import {
  derivePreferredEenEventType,
  PERSON_EVENT_TYPE,
  MOTION_EVENT_TYPE,
} from "../smart_zones/eenEventsHelper";

export default {
  name: "CardTaskEventVideos",
  components: {
    DialogLayout,
    CardLayout,
  },
  props: {
    taskId: {
      type: String,
      required: true,
    },
    incidentId: {
      type: String,
      required: true,
    },
  },
  setup() {
    const { facilityFormatDateTime4Humans, currentFacility } = useFacility();
    return {
      currentFacility,
      facilityFormatDateTime4Humans,
    };
  },
  data() {
    return {
      loading: true,
      loadingMore: false,
      loadingVideo: false,
      videoDialogOpen: false,
      thumbnailsList: [],
      nextPageToken: null,
      videoData: null,
      videoKey: 0,
      pageSize: 10,
      selectedThumb: null,
      taskDetails: null,
      directHistoryUrl: null,
      noEventsDetected: false,
      iframeLoaded: false,
      iframeAuthSent: false,
      historyIframeKey: 0,
      authToken: null,
      eenVideoRetentionDays: 7,
      incidentDetails: null,
      currentEventType: null,
      allowFallbackToMotion: true,
      hasBothEventTypes: false,
      selectedEventType: null,
    };
  },

  computed: {
    isIncidentWithinRetentionPeriod() {
      if (!this.incidentDetails || !this.incidentDetails.startedAt) {
        return false;
      }
      return this.isVideoAvailable(this.incidentDetails.startedAt);
    },
    isVideoCameraEnabled() {
      if (!this.taskDetails?.findTask?.organization?.devices) return false;
      return !!this.taskDetails.findTask.organization.devices.find(
        (device) => device.type === "een" && device.subtype === "video_camera"
      );
    },
  },

  watch: {
    selectedEventType(newType) {
      if (!newType || !this.hasBothEventTypes) return;
      this.currentEventType = newType;
      this.thumbnailsList = [];
      this.nextPageToken = null;
      this.noEventsDetected = false;
      this.loading = true;
      this.fetchEventVideos().finally(() => {
        this.loading = false;
      });
    },
  },

  apollo: {
    taskDetails: {
      query: GET_TASK_INCIDENTS,
      variables() {
        return {
          id: this.taskId,
          incidentId: this.incidentId,
        };
      },
      update(data) {
        this.taskDetails = data.findTask;
        this.incidentDetails = data.incident;
        return data;
      },
      result(result) {
        // Only fetch videos if we haven't already and data exists
        if (result.data && !this.dataFetchAttempted) {
          this.fetchEventVideos();
        }
      },
    },
  },
  mounted() {
    if (!EEN_AUTH_STATUS) {
      import("../fixtures/graphql").then(({ EEN_AUTH_STATUS: authQuery }) => {
        this.EEN_AUTH_STATUS = authQuery;
      });
    }
  },
  beforeDestroy() {
    if (this._messageListenerAdded) {
      window.removeEventListener("message", this.handleHistoryIframeMessage);
    }
  },
  methods: {
    isVideoAvailable(startTimestamp) {
      if (!this.eenVideoRetentionDays) return false;
      if (!startTimestamp) return false;

      const now = new Date();
      const startDate = new Date(startTimestamp);
      const diffInDays = (now - startDate) / (1000 * 60 * 60 * 24);

      return diffInDays <= this.eenVideoRetentionDays;
    },

    formatTimestamp(timestamp) {
      if (!timestamp) return "N/A";
      try {
        if (
          typeof timestamp === "string" &&
          (timestamp.includes("Invalid") || timestamp.includes("undefined"))
        ) {
          return "N/A";
        }
        const date = new Date(timestamp);
        if (isNaN(date.getTime())) {
          return "N/A";
        }
        return this.facilityFormatDateTime4Humans(
          date.toISOString(),
          false,
          true,
          true
        );
      } catch (error) {
        console.error(`Error formatting timestamp (${timestamp}):`, error);
        return "N/A";
      }
    },

    async fetchEventVideos(pageToken = null) {
      if (!this.taskDetails || !this.incidentDetails) {
        if (this.$apollo.queries.taskDetails.loading === false) {
          this.loading = false;
        }
        return;
      }

      try {
        if (!pageToken) {
          this.loading = true;
        }
        const eenDevice = this.taskDetails.findTask.organization.devices?.find(
          (device) => device.type === "een"
        );

        const actorId = eenDevice?.externalId
          ? `camera:${eenDevice.externalId}`
          : null;

        if (!actorId) {
          this.loading = false;
          return;
        }

        const organizationId =
          this.taskDetails.findTask.organization.ancestors[0].id;
        const esn = actorId.replace("camera:", "");

        const formatTimestamp = (timestamp) => {
          if (!timestamp) return null;
          try {
            const date = new Date(timestamp);
            return date.toISOString();
          } catch (e) {
            console.error("Error formatting timestamp:", e);
            return null;
          }
        };

        const startTimestamp = formatTimestamp(this.incidentDetails.startedAt);
        const endTimestamp = formatTimestamp(this.incidentDetails.closedAt);

        if (this.isVideoAvailable(this.incidentDetails.startedAt)) {
          this.directHistoryUrl = `https://iframe.eagleeyenetworks.com/#/history?ids=${esn}&time=${encodeURIComponent(
            startTimestamp
          )}`;
        } else {
          this.directHistoryUrl = null;
        }

        // Only fetch videos if the incident is within the retention period
        if (!this.isVideoAvailable(this.incidentDetails.startedAt)) {
          this.thumbnailsList = [];
          this.noEventsDetected = true;
          this.loading = false;
          return;
        }

        if (!pageToken) {
          const { preferred, allowFallbackToMotion, hasPerson, hasMotion } =
            derivePreferredEenEventType(this.incidentDetails?.events || []);
          this.hasBothEventTypes = hasPerson && hasMotion;
          this.allowFallbackToMotion = allowFallbackToMotion;

          if (this.hasBothEventTypes) {
            if (!this.selectedEventType) {
              this.selectedEventType = MOTION_EVENT_TYPE;
            }
            this.currentEventType = this.selectedEventType;
          } else {
            this.currentEventType = preferred || PERSON_EVENT_TYPE;
          }
        }

        let results = [];
        let nextPageToken = null;

        const fetchEvents = async (eventType, token = null) => {
          const { data } = await this.$apollo.query({
            query: GET_ALL_EEN_EVENTS,
            variables: {
              organizationId: organizationId,
              actor: actorId,
              eventType,
              pageToken: token,
              pageSize: this.pageSize,
              startTimestamp: startTimestamp,
              endTimestamp: endTimestamp,
            },
            fetchPolicy: "network-only",
          });

          return {
            results: data?.eenAllEvents?.results || [],
            nextPageToken: data?.eenAllEvents?.nextPageToken || null,
          };
        };

        const eventTypeToUse = this.currentEventType;
        const secondQueryType =
          eventTypeToUse === PERSON_EVENT_TYPE
            ? MOTION_EVENT_TYPE
            : PERSON_EVENT_TYPE;

        let response = await fetchEvents(eventTypeToUse, pageToken);
        results = response.results;
        nextPageToken = response.nextPageToken;

        if (
          !pageToken &&
          !this.hasBothEventTypes &&
          this.allowFallbackToMotion &&
          results.length === 0
        ) {
          response = await fetchEvents(secondQueryType, null);
          if (response.results.length > 0) {
            results = response.results;
            nextPageToken = response.nextPageToken;
            this.currentEventType = secondQueryType;
          }
        }

        this.nextPageToken = nextPageToken;

        const enhancedResults = results.map((result) => ({
          ...result,
          organizationId: organizationId,
          deviceLocation: this.incidentDetails.camera?.name,
          incidentType: this.incidentDetails.type?.name,
        }));

        if (pageToken) {
          this.thumbnailsList = [...this.thumbnailsList, ...enhancedResults];
        } else {
          this.thumbnailsList = enhancedResults;
        }

        if (this.thumbnailsList.length === 0) {
          this.noEventsDetected = true;
          this.iframeAuthSent = false;
          this.historyIframeKey++;
          setTimeout(() => this.setupHistoryIframeListener(), 500);
        } else {
          this.noEventsDetected = false;
        }
      } catch (error) {
        console.error("Error fetching event videos:", error);
        this.noEventsDetected = true;
      } finally {
        this.loading = false;
        this.loadingMore = false;
      }
    },

    loadMoreThumbnails() {
      if (this.nextPageToken) {
        this.loadingMore = true;
        this.fetchEventVideos(this.nextPageToken);
      }
    },

    async openVideo(thumb) {
      if (!thumb) return;

      // Reset video data
      this.videoData = null;

      // Open dialog immediately
      this.videoDialogOpen = true;
      this.loadingVideo = true;

      try {
        const { data } = await this.$apollo.query({
          query: EEN_VIDEO_FEED,
          variables: {
            fromTimeStamp: thumb.startTimestamp,
            deviceId: thumb.actorId.replace("camera:", ""),
            organizationId: thumb.organizationId,
          },
          fetchPolicy: "network-only",
        });

        this.videoData = data?.eenVideoFeed?.video || null;
        this.videoKey++;
      } catch (error) {
        console.error("Error fetching video:", error);
      } finally {
        this.loadingVideo = false;
      }
    },

    setupHistoryIframeListener() {
      const iframe = document.getElementById("sd_direct_history_iframe_1");
      if (iframe) {
        if (!this._messageListenerAdded) {
          window.addEventListener("message", this.handleHistoryIframeMessage);
          this._messageListenerAdded = true;
        }

        this.sendTokenToHistoryIframe();
      } else {
        setTimeout(() => this.setupHistoryIframeListener(), 200);
      }
    },

    handleHistoryIframeMessage(event) {
      if (event.origin !== "https://iframe.eagleeyenetworks.com") return;

      if (
        event.data === "een-iframe-loaded" ||
        event.data === "een-iframe-token-expired"
      ) {
        this.sendTokenToHistoryIframe();
      }
    },

    async sendTokenToHistoryIframe() {
      const iframe = document.getElementById("sd_direct_history_iframe_1");

      try {
        if (!this.authToken) {
          const { data } = await this.$apollo.query({
            query: EEN_AUTH_STATUS,
            variables: {
              organization_id:
                this.taskDetails.findTask.organization.ancestors[0].id,
            },
            fetchPolicy: "network-only",
          });

          this.authToken = data?.eenAuthStatus?.accessToken;
        }

        if (iframe && iframe.contentWindow && this.authToken) {
          iframe.contentWindow.postMessage(
            {
              type: "een-token",
              token: this.authToken,
            },
            "https://iframe.eagleeyenetworks.com"
          );

          // Send token again after a short delay to ensure it's received
          setTimeout(() => {
            if (iframe && iframe.contentWindow) {
              iframe.contentWindow.postMessage(
                {
                  type: "een-token",
                  token: this.authToken,
                },
                "https://iframe.eagleeyenetworks.com"
              );
            }
          }, 1000);
        }
      } catch (error) {
        console.error("Error sending token to history iframe:", error);
      }
    },
  },
};
</script>

<style scoped>
.event-videos-container {
  margin-top: 16px;
}

.event-thumbnail-card {
  transition: transform 0.2s;
  cursor: pointer;
}

.event-thumbnail-card:hover {
  transform: translateY(-4px);
}

.video-player {
  width: 100%;
  max-height: 500px;
  background-color: #000;
}

.event-thumbnail {
  position: relative;
}

.video-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.3);
  transition: opacity 0.2s;
}

.event-thumbnail:hover .video-overlay {
  opacity: 1;
}

.play-button {
  opacity: 0.9;
  transform: scale(1);
  transition: transform 0.2s;
}

.play-button:hover {
  transform: scale(1.2);
}
</style>
